import { getTenantId, setLocalTenantId } from "@/lib/storeManager";
import { createContext, ReactNode, useEffect, useState } from "react";

interface TenantContextType {
  tenantId: string | null;
  setTenantId: (id: string) => void;
  clearTenantId: () => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

const envTenantId = () => {
  const id = import.meta.env.VITE_X_TENANTID;
  return typeof id === "string" && id.trim() ? id.trim() : null;
};

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenantId, setTenantIdState] = useState<string | null>(() => {
    return envTenantId() ?? getTenantId();
  });

  const setTenantIdHandler = (id: string) => {
    setLocalTenantId(id);
    setTenantIdState(id);
  };

  const clearTenantId = () => {
    localStorage.removeItem("tenantId");
    // Keep env tenant as source of truth when clearing local override
    setTenantIdState(envTenantId());
  };

  // Sync tenant from VITE_X_TENANTID (no URL-based tenant)
  useEffect(() => {
    const fromEnv = envTenantId();
    if (fromEnv) {
      setTenantIdHandler(fromEnv);
    }
  }, []);

  return (
    <TenantContext.Provider
      value={{
        tenantId,
        setTenantId: setTenantIdHandler,
        clearTenantId,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

// Export the context for use in other files
export { TenantContext };
